import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Button,
  CircularProgress,
  Pagination,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Stack,
  Card,
  Grid
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import API from "../api";
import { printReceipt } from "../utils/printer";

/**
 * Safely extracts human-readable error messages from Axios / FastAPI payloads.
 */
const extractErrorMessage = (err, fallbackMsg = "An unexpected error occurred.") => {
  const detail = err?.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail) && detail.length > 0) {
    const firstErr = detail[0];
    if (typeof firstErr === "string") return firstErr;
    if (firstErr && typeof firstErr === "object" && firstErr.msg) {
      const locPath = Array.isArray(firstErr.loc) ? `${firstErr.loc.join(" -> ")}: ` : "";
      return `${locPath}${firstErr.msg}`;
    }
  }

  if (detail && typeof detail === "object" && detail.msg) {
    return detail.msg;
  }

  return err?.message || fallbackMsg;
};

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Search & Filter States
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Refund Management Modal States
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [refundQuantities, setRefundQuantities] = useState({});
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  // Notification Toast
  const [notification, setNotification] = useState({ open: false, message: "", severity: "info" });

  const showNotification = useCallback((message, severity = "info") => {
    const cleanMsg = typeof message === "string" ? message : JSON.stringify(message);
    setNotification({ open: true, message: cleanMsg, severity });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Helper: Safely resolve active Tenant ID label
  const getActiveTenantId = () => {
    return localStorage.getItem("tenant_id") || "default";
  };

  // Fetch Data: Reads paginated sales logs
  const fetchSales = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pageNum, limit: 10 };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const res = await API.get("/sales/", { params });

      if (Array.isArray(res.data)) {
        setSales(res.data);
        setTotalPages(1);
      } else if (res.data && typeof res.data === "object") {
        setSales(res.data.sales || []);
        setTotalPages(res.data.pages || 1);
      } else {
        setSales([]);
      }
    } catch (err) {
      console.error("Failed to fetch sales logs:", err);
      setError(extractErrorMessage(err, "Failed to fetch sales logs from the workspace backend."));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, searchQuery]);

  useEffect(() => {
    fetchSales(page);
  }, [page, fetchSales]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchSales(1);
  };

  // Safely calculate current page grand total
  const grandTotal = Array.isArray(sales)
    ? sales.reduce((acc, sale) => acc + Number(sale.total || 0), 0)
    : 0;

  // ==========================================
  // REFUND MANAGEMENT
  // ==========================================
  const openRefundDialog = (sale) => {
    setSelectedSale(sale);
    const initialQuantities = {};
    (sale.items || []).forEach((item) => {
      initialQuantities[item.product_id] = 0;
    });
    setRefundQuantities(initialQuantities);
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    if (refundSubmitting) return;
    setRefundDialogOpen(false);
    setSelectedSale(null);
    setRefundQuantities({});
  };

  const handleQtyChange = (productId, val, maxQty) => {
    if (val === "") {
      setRefundQuantities((prev) => ({ ...prev, [productId]: "" }));
      return;
    }
    const parsed = parseInt(val, 10) || 0;
    const bounded = Math.min(Math.max(0, parsed), maxQty);
    setRefundQuantities((prev) => ({ ...prev, [productId]: bounded }));
  };

  const submitRefundRequest = async () => {
    const itemsToRefund = Object.entries(refundQuantities)
      .map(([productId, qty]) => ({
        product_id: parseInt(productId, 10),
        quantity_to_refund: Number(qty) || 0
      }))
      .filter((item) => item.quantity_to_refund > 0);

    if (itemsToRefund.length === 0) {
      showNotification("Please select at least one item quantity to refund.", "warning");
      return;
    }

    setRefundSubmitting(true);
    try {
      const response = await API.post("/sales/refund", {
        sale_id: selectedSale.id,
        items_to_refund: itemsToRefund
      });

      if (response.data?.status === "SUCCESS" || response.data?.refunded_amount !== undefined) {
        const returnedAmount = Number(response.data?.refunded_amount || 0).toFixed(2);
        showNotification(`Refund processed successfully! Total returned: $${returnedAmount}`, "success");
        closeRefundDialog();
        fetchSales(page);
      } else {
        showNotification(response.data?.message || "Refund completed.", "info");
        closeRefundDialog();
        fetchSales(page);
      }
    } catch (err) {
      console.error("Refund submission error:", err);
      showNotification(extractErrorMessage(err, "Permission denied or invalid refund parameters."), "error");
    } finally {
      setRefundSubmitting(false);
    }
  };

  // ==========================================
  // CSV EXPORT & RECEIPT PRINTING
  // ==========================================
  const exportToCSV = () => {
    const headers = [
      "Sale ID",
      "Cashier",
      "Product Name",
      "Quantity",
      "Price",
      "Payment Method",
      "Total",
      "Tendered",
      "Change",
      "Timestamp"
    ];

    const rows = sales.flatMap((sale) =>
      (sale.items || []).map((item) => [
        sale.id,
        `"${(sale.username || "Cashier").replace(/"/g, '""')}"`,
        `"${(item.productname || item.product_name || "Unknown Product").replace(/"/g, '""')}"`,
        item.quantity || 0,
        Number(item.price || 0).toFixed(2),
        sale.payment_method || "N/A",
        Number(sale.total || 0).toFixed(2),
        Number(sale.tendered || 0).toFixed(2),
        Number(sale.change || 0).toFixed(2),
        `"${new Date(sale.timestamp).toLocaleString()}"`
      ])
    );

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `sales_history_${getActiveTenantId()}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = async (sale) => {
    try {
      const normalizedSale = {
        ...sale,
        items: (sale.items || []).map((item) => ({
          ...item,
          productname: item.productname || item.product_name || "Item"
        }))
      };
      await printReceipt({ sale: normalizedSale });
    } catch (err) {
      console.error("Receipt printing failure:", err);
      showNotification(`Failed to print receipt: ${err.message || "Printer service unreachable"}`, "error");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Bar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Sales Operations & History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Workspace Boundary: <strong>{getActiveTenantId().toUpperCase()}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={exportToCSV}
          disabled={!sales.length || loading}
          sx={{ fontWeight: "bold", textTransform: "none" }}
        >
          Export Ledger (CSV)
        </Button>
      </Box>

      {/* Filter Options */}
      <Card elevation={0} sx={{ p: 2, mb: 3, border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}>
        <Box component="form" onSubmit={handleFilterSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Search Invoice / Cashier"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                type="submit"
                variant="outlined"
                startIcon={<FilterAltIcon />}
                fullWidth
                sx={{ height: 40, textTransform: "none" }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Main Table Content */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell><strong>Invoice ID</strong></TableCell>
                  <TableCell><strong>Cashier</strong></TableCell>
                  <TableCell><strong>Items Summary</strong></TableCell>
                  <TableCell><strong>Method</strong></TableCell>
                  <TableCell><strong>Total Amount</strong></TableCell>
                  <TableCell><strong>Tendered</strong></TableCell>
                  <TableCell><strong>Change</strong></TableCell>
                  <TableCell><strong>Date & Time</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, color: "text.secondary" }}>
                      No transaction records located for this tenant workspace.
                    </TableCell>
                  </TableRow>
                ) : (
                  sales.map((sale) => (
                    <TableRow key={sale.id} hover>
                      <TableCell sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                        #{sale.id}
                      </TableCell>
                      <TableCell>{sale.username || "Cashier"}</TableCell>
                      <TableCell>
                        <Box component="ul" sx={{ pl: 2, m: 0, fontSize: "0.85rem" }}>
                          {(sale.items || []).map((item, idx) => (
                            <li key={idx}>
                              {item.productname || item.product_name || "Item"} — Qty: <strong>{item.quantity}</strong> @ ${Number(item.price || 0).toFixed(2)}
                            </li>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(sale.payment_method || "CASH").toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.75rem", fontWeight: "bold" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                        ${Number(sale.total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>${Number(sale.tendered || 0).toFixed(2)}</TableCell>
                      <TableCell>${Number(sale.change || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                        {sale.timestamp ? new Date(sale.timestamp).toLocaleString() : "N/A"}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<LocalPrintshopIcon />}
                            onClick={() => handlePrint(sale)}
                            sx={{ textTransform: "none" }}
                          >
                            Print
                          </Button>
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<UndoIcon />}
                            onClick={() => openRefundDialog(sale)}
                            sx={{ textTransform: "none", fontWeight: "bold" }}
                          >
                            Refund
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}

                {sales.length > 0 && (
                  <TableRow sx={{ backgroundColor: "action.selected" }}>
                    <TableCell colSpan={4} align="right">
                      <strong>Current Page Total:</strong>
                    </TableCell>
                    <TableCell colSpan={5}>
                      <Typography component="span" sx={{ color: "success.main", fontWeight: "bold", fontSize: "1.1rem" }}>
                        ${grandTotal.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              sx={{ mt: 4, mb: 4, display: "flex", justifyContent: "center" }}
            />
          )}
        </>
      )}

      {/* Itemized Refund Modal */}
      <Dialog open={refundDialogOpen} onClose={closeRefundDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: "bold", backgroundColor: "action.hover" }}>
          Process Item Refund (Invoice #{selectedSale?.id})
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Specify quantity to restock into shelf inventory. Reversals require Supervisor credentials or authorization.
          </Typography>

          {(selectedSale?.items || []).map((item) => (
            <Box
              key={item.product_id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                pb: 1,
                borderBottom: "1px solid",
                borderColor: "divider"
              }}
            >
              <Box sx={{ flexGrow: 1, pr: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.productname || item.product_name || "Item"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Purchased Qty: {item.quantity} | Unit Price: ${Number(item.price || 0).toFixed(2)}
                </Typography>
              </Box>
              <TextField
                label="Refund Qty"
                type="number"
                size="small"
                disabled={refundSubmitting}
                value={refundQuantities[item.product_id] ?? 0}
                onChange={(e) => handleQtyChange(item.product_id, e.target.value, item.quantity)}
                inputProps={{ min: 0, max: item.quantity }}
                sx={{ width: 110 }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: "action.hover" }}>
          <Button onClick={closeRefundDialog} variant="outlined" color="inherit" disabled={refundSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={submitRefundRequest}
            variant="contained"
            color="warning"
            disabled={refundSubmitting}
            startIcon={refundSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ fontWeight: "bold" }}
          >
            {refundSubmitting ? "Processing..." : "Confirm Refund & Restock"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Toast */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}